import { AtSymbolIcon, PencilIcon, PhoneIcon } from '@heroicons/react/24/solid';
import React from 'react';

import type { Owner } from '../../../../@types/Owner';
import Badge from '../../../common/Badge';

interface OwnerInfosProps {
  owner?: Owner;
  showContacts?: boolean;
  showEditButton?: boolean;
  onEditClick?: (owner: Owner) => void;
}

const OwnerInfos: React.FC<OwnerInfosProps> = ({ 
  owner, 
  showContacts = false,
  showEditButton = false,
  onEditClick,
}) => {
  if (!owner) {
    return (
      <div>
        <div className="mb-1 font-medium text-gray-900">Propriétaire non défini</div>
      </div>
    );
  }

  const getCivilityLabel = (civility?: number): string => {
    if (!civility) {return '';}
    switch (civility) {
    case 1: return 'M.';
    case 2: return 'Mme';
    case 3: return 'Société';
    default: return '';
    }
  };

  const getFullName = (ownerWithFullName: Owner): string => {
    const civility = getCivilityLabel(ownerWithFullName.civility);
    const firstname = ownerWithFullName.firstname || '';
    const lastname = ownerWithFullName.lastname || '';
    return `${civility} ${firstname} ${lastname}`.trim() || 'Propriétaire non identifié';
  };

  const handleEditClick = () => {
    if (onEditClick && owner) {
      onEditClick(owner);
    }
  };

  const getFullAddress = (): string => {
    const address = owner.address || '';
    const postalCode = owner.postalCode || '';
    const city = owner.city || '';
    
    if (!address && !postalCode && !city) {
      return 'Adresse non renseignée';
    }
    
    return [address, postalCode, city].filter(Boolean).join(', ');
  };

  return (
    <div className="flex items-start justify-between">
      <div>
        <div className="font-medium text-gray-900">
          {getFullName(owner)}
          <br/>
          {owner.livingIn !== undefined && owner.livingIn !== null && (
            <span className={`px-2 py-1 text-xs font-medium ${owner.livingIn ? 'bg-blue-100 text-blue-800' : 'bg-red-100 text-red-800'} rounded-full`}>
              {owner.livingIn ? 'Résident' : 'Non résident'}
            </span>
          )}
        </div>
        
        {owner.livingIn === false && (
          <div className="mt-1 text-sm text-gray-600">
            <p className="italic">{getFullAddress()}</p>
          </div>
        )}
        
        {showContacts && (
          <div className="mt-1 text-sm text-gray-600">
            {owner.phone && (
              <div className="flex items-center">
                <PhoneIcon className="mr-2 h-4 w-4 shrink-0 text-gray-500" />
                <a href={`tel:${owner.phone}`} className="text-rgpd-blue hover:underline">
                  {owner.phone}
                </a>
              </div>
            )}
            {owner.email && (
              <div className="flex items-center">
                <AtSymbolIcon className="mr-2 h-4 w-4 shrink-0 text-gray-500" />
                <a href={`mailto:${owner.email}`} className="text-rgpd-blue hover:underline">
                  {owner.email}
                </a>
              </div>
            )}
          </div>
        )}
        
        <div className="mt-1 flex flex-wrap items-center gap-1">
          {owner.isPotentialOwner !== undefined && owner.isPotentialOwner !== null ? (
            owner.isPotentialOwner ? (
              <Badge mode="warning" text="Propriétaire potentiel" size="small"/>
            ) : (
              <Badge mode="success" text="Propriétaire certain" size="small"/>
            )
          ) : (
            <Badge mode="warning" text="Statut non défini" size="small"/>
          )}
          {owner.source === 'dpe' && (
            <Badge mode="info" text="DPE" size="small"/>
          )}
          {owner.source === 'incoming-call' && (
            <Badge mode="info" text="Appel entrant" size="small"/>
          )}
          {owner.source === 'dpe-general-mailing' && (
            <Badge mode="info" text="Envoi général DPE" size="small"/>
          )}
        </div>
      </div>
      
      {showEditButton && (
        <button
          onClick={handleEditClick}
          className="rounded-md bg-gray-100 p-1 text-gray-500 hover:bg-gray-200 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 focus:outline-none"
          title="Modifier les informations"
        >
          <PencilIcon className="h-4 w-4" />
        </button>
      )}
    </div>
  );
};

export default OwnerInfos; 